/**
 * Sprachbefehle für die Schnellaufnahme (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 4.3).
 *
 * Zwei Befehle, mehr nicht: „neue Erinnerung …" und „neue Notiz …". Sie brauchen eine
 * Stelle, an der diktiert werden kann, ohne dass schon feststeht, wohin das Ergebnis
 * gehört — in der Liste und im Editor entscheidet weiterhin der Ort.
 *
 * Ohne Befehl wird nicht geraten, sondern gefragt: Erinnerung oder Notiz. Ein falsch
 * abgelegter Zuruf ist ärgerlicher als eine Rückfrage, die einen Tastendruck kostet.
 */

export type CaptureTarget = 'note' | 'reminder';

export type Capture = {
  target: CaptureTarget;
  /** Der Rest des Satzes ohne den einleitenden Befehl. */
  text: string;
  /** Ob der Befehl ausdrücklich gesprochen wurde. */
  explicit: boolean;
};

const NOTE = /^\s*(?:mach\s+)?(?:eine\s+|ein\s+)?neue[rs]?\s+notiz\b[:,.]?\s*/i;
const REMINDER =
  /^\s*(?:mach\s+)?(?:eine\s+)?neue\s+erinnerung\b[:,.]?\s*|^\s*erinner(?:e)?\s+mich\b[:,.]?\s*/i;

export function readCommand(input: string): Capture {
  const note = NOTE.exec(input);
  if (note) return { target: 'note', text: input.slice(note[0].length).trim(), explicit: true };

  const reminder = REMINDER.exec(input);
  // Beim Erinnerungsbefehl bleibt der Wortlaut stehen: `parseReminder` räumt die
  // Floskel selbst ab und braucht dafür den ganzen Satz.
  if (reminder) return { target: 'reminder', text: input.trim(), explicit: true };

  return { target: 'reminder', text: input.trim(), explicit: false };
}

/**
 * Aus einem diktierten Absatz wird eine Notiz mit Überschrift. Ohne sie stünde der
 * gesamte Text als Titel in der Liste — Titel werden aus der ersten Zeile abgeleitet.
 *
 * Ist eine Überschrift vorgegeben (vom Server, siehe `api.title`), bleibt der
 * gesprochene Text vollständig darunter stehen. Ohne sie wird der erste Satz zur
 * Überschrift und aus dem Text herausgenommen — sonst stünde er doppelt da.
 */
export function noteFromDictation(text: string, headline?: string | null): string {
  const clean = text.trim();
  if (clean === '') return '';

  if (headline) return `# ${headline}\n\n${clean}\n`;

  const stop = clean.search(/[.!?](\s|$)/);
  const cut = stop > 0 && stop < 80 ? stop + 1 : -1;
  const title = (cut > 0 ? clean.slice(0, cut - 1) : clean).slice(0, 80).trim();
  const rest = cut > 0 ? clean.slice(cut).trim() : '';

  return `# ${title}\n\n${rest}${rest ? '\n' : ''}`;
}
